import axios from "axios";
import {defineStore} from "pinia";
import {authHeader} from "@/helpers/authservice/auth-header";
import {requiredHeaders} from "@/helpers/authservice/requiredHeaders";
import {apiRequestsHelper} from "@/state/pinia/apiRequestHelper";

// API uses 1-based paging for RBAC endpoints.
const sanitizePositiveInteger = (value, fallback) => {
    const parsed = parseInt(value, 10);
    if (Number.isFinite(parsed) && parsed > 0) {
        return parsed;
    }
    return fallback;
};

const sanitizeSortingKey = (value) => {
    if (!value) return null;
    return encodeURIComponent(value);
};

const sanitizeSortingDirection = (value) => {
    if (!value) return null;
    return value === "DESC" ? "DESC" : "ASC";
};

export const useUserRbacManagerModule = defineStore("userRbacManagerModule", {
    state: () => ({
        users_endpoint: "/api/users",
        rbac_endpoint: "/api/rbac",
        users: [],
        usersPagesCount: 0,
        usersDataTotal: 0,
        roles: [],
        rolesPagesCount: 0,
        rolesDataTotal: 0,
        privileges: [],
        privilegesPagesCount: 0,
        privilegesDataTotal: 0,
        rolesCatalog: [],
        privilegesCatalog: [],
    }),
    getters: {
        getUsers: (state) => state.users,
        getUsersPagesCount: (state) => state.usersPagesCount,
        getUsersDataTotal: (state) => state.usersDataTotal,
        getRoles: (state) => state.roles,
        getRolesPagesCount: (state) => state.rolesPagesCount,
        getRolesDataTotal: (state) => state.rolesDataTotal,
        getPrivileges: (state) => state.privileges,
        getPrivilegesPagesCount: (state) => state.privilegesPagesCount,
        getPrivilegesDataTotal: (state) => state.privilegesDataTotal,
        getRolesCatalog: (state) => state.rolesCatalog,
        getPrivilegesCatalog: (state) => state.privilegesCatalog,
    },
    actions: {
        setUsers(users) {
            this.users = users;
        },
        setUsersPagesCount(count) {
            this.usersPagesCount = count;
        },
        setUsersDataTotal(count) {
            this.usersDataTotal = count;
        },
        setRoles(roles) {
            this.roles = roles;
        },
        setRolesPagesCount(count) {
            this.rolesPagesCount = count;
        },
        setRolesDataTotal(count) {
            this.rolesDataTotal = count;
        },
        setPrivileges(privileges) {
            this.privileges = privileges;
        },
        setPrivilegesPagesCount(count) {
            this.privilegesPagesCount = count;
        },
        setPrivilegesDataTotal(count) {
            this.privilegesDataTotal = count;
        },
        setRolesCatalog(roles) {
            this.rolesCatalog = roles;
        },
        setPrivilegesCatalog(privileges) {
            this.privilegesCatalog = privileges;
        },

        httpGetUsersPaging(payload) {
            const currentPage = sanitizePositiveInteger(payload.currentPage, 1);
            const pageSize = sanitizePositiveInteger(payload.pageSize, 20);
            let endpoint = `${this.users_endpoint}/all/pages?page=${currentPage}&size=${pageSize}`;
            const sortingKey = sanitizeSortingKey(payload.sortingKey);
            if (sortingKey) {
                endpoint += `&sortingKey=${sortingKey}`;
            }
            const sortingDirection = sanitizeSortingDirection(payload.sortingDirection);
            if (sortingDirection) {
                endpoint += `&sortingDirection=${sortingDirection}`;
            }
            console.log("Get users: ", endpoint);
            return axios({
                method: "get",
                url: endpoint,
                headers: authHeader(),
            })
                .then((response) => {
                    if (response.status === 200) {
                        const payloadData = response.data?.payload || response.data;
                        if (payloadData?.content) {
                            this.setUsers(payloadData.content);
                            this.setUsersPagesCount(payloadData.totalPages || 0);
                            this.setUsersDataTotal(payloadData.totalElements || 0);
                        } else if (Array.isArray(payloadData)) {
                            this.setUsers(payloadData);
                            this.setUsersPagesCount(1);
                            this.setUsersDataTotal(payloadData.length);
                        }
                    }
                    return apiRequestsHelper.handleResponseWithDialogs(response, false);
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpGetUsersPaging");
                });
        },

        httpGetRolesPaging(payload) {
            const currentPage = sanitizePositiveInteger(payload.currentPage, 1);
            const pageSize = sanitizePositiveInteger(payload.pageSize, 20);
            const groupParam = payload.group ? `&group=${encodeURIComponent(payload.group)}` : "";
            let endpoint = `${this.rbac_endpoint}/roles/pages?page=${currentPage}&size=${pageSize}${groupParam}`;
            const sortingKey = sanitizeSortingKey(payload.sortingKey);
            if (sortingKey) {
                endpoint += `&sortingKey=${sortingKey}`;
            }
            const sortingDirection = sanitizeSortingDirection(payload.sortingDirection);
            if (sortingDirection) {
                endpoint += `&sortingDirection=${sortingDirection}`;
            }

            return axios({
                method: "get",
                url: endpoint,
                headers: authHeader(),
            })
                .then((response) => {
                    if (response.status === 200) {
                        const payloadData = response.data?.payload || response.data;
                        if (payloadData?.content) {
                            this.setRoles(payloadData.content);
                            this.setRolesPagesCount(payloadData.totalPages || 0);
                            this.setRolesDataTotal(payloadData.totalElements || 0);
                        }
                    }
                    return apiRequestsHelper.handleResponseWithDialogs(response, false);
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpGetRolesPaging");
                });
        },

        httpGetRoles() {
            return axios({
                method: "get",
                url: `${this.rbac_endpoint}/roles`,
                headers: authHeader(),
            })
                .then((response) => {
                    if (response.status === 200) {
                        const payloadData = response.data?.payload || response.data;
                        if (Array.isArray(payloadData)) {
                            this.setRolesCatalog(payloadData);
                        }
                    }
                    return apiRequestsHelper.handleResponseWithDialogs(response, false);
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpGetRoles");
                });
        },

        httpGetRoleById(payload) {
            return axios({
                method: "get",
                url: `${this.rbac_endpoint}/roles/${payload.id}`,
                headers: authHeader(),
            })
                .then((response) => {
                    return apiRequestsHelper.handleResponseWithDialogs(response, false);
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpGetRoleById");
                });
        },

        httpCreateRole(payload) {
            return axios({
                method: "post",
                url: `${this.rbac_endpoint}/roles`,
                headers: requiredHeaders(),
                data: payload,
            })
                .then((response) => {
                    return apiRequestsHelper.handleResponseWithDialogs(response, true);
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpCreateRole");
                });
        },

        httpUpdateRole(payload) {
            return axios({
                method: "put",
                url: `${this.rbac_endpoint}/roles/${payload.id}`,
                headers: requiredHeaders(),
                data: payload.data,
            })
                .then((response) => {
                    return apiRequestsHelper.handleResponseWithDialogs(response, true);
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpUpdateRole");
                });
        },

        httpDeleteRole(payload) {
            return axios({
                method: "delete",
                url: `${this.rbac_endpoint}/roles/${payload.id}`,
                headers: requiredHeaders(),
            })
                .then((response) => {
                    return apiRequestsHelper.handleResponseWithDialogs(response, true);
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpDeleteRole");
                });
        },

        httpGetRolePrivileges(payload) {
            const page = payload.page || 1;
            const size = payload.size || 20;
            return axios({
                method: "get",
                url: `${this.rbac_endpoint}/roles/${payload.id}/privileges/pages?page=${page}&size=${size}`,
                headers: authHeader(),
            })
                .then((response) => {
                    return apiRequestsHelper.handleResponseWithDialogs(response, false);
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpGetRolePrivileges");
                });
        },

        httpUpdateRolePrivilegesReplace(payload) {
            return axios({
                method: "put",
                url: `${this.rbac_endpoint}/roles/${payload.id}/privileges`,
                headers: requiredHeaders(),
                data: {privilegeKeys: payload.privilegeKeys},
            })
                .then((response) => {
                    return apiRequestsHelper.handleResponseWithDialogs(response, true);
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpUpdateRolePrivilegesReplace");
                });
        },

        httpUpdateRolePrivilegesAdd(payload) {
            return axios({
                method: "patch",
                url: `${this.rbac_endpoint}/roles/${payload.id}/privileges`,
                headers: requiredHeaders(),
                data: {privilegeKeys: payload.privilegeKeys},
            })
                .then((response) => {
                    return apiRequestsHelper.handleResponseWithDialogs(response, true);
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpUpdateRolePrivilegesAdd");
                });
        },

        httpUpdateRolePrivilegesRemove(payload) {
            return axios({
                method: "patch",
                url: `${this.rbac_endpoint}/roles/${payload.id}/privileges/remove`,
                headers: requiredHeaders(),
                data: {privilegeKeys: payload.privilegeKeys},
            })
                .then((response) => {
                    return apiRequestsHelper.handleResponseWithDialogs(response, true);
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpUpdateRolePrivilegesRemove");
                });
        },

        httpGetPrivilegesPaging(payload) {
            const currentPage = sanitizePositiveInteger(payload.currentPage, 1);
            const pageSize = sanitizePositiveInteger(payload.pageSize, 20);
            let endpoint = `${this.rbac_endpoint}/privileges/pages?page=${currentPage}&size=${pageSize}`;
            const sortingKey = sanitizeSortingKey(payload.sortingKey);
            if (sortingKey) {
                endpoint += `&sortingKey=${sortingKey}`;
            }
            const sortingDirection = sanitizeSortingDirection(payload.sortingDirection);
            if (sortingDirection) {
                endpoint += `&sortingDirection=${sortingDirection}`;
            }
            console.log("Get privileges: ", endpoint);

            return axios({
                method: "get",
                url: endpoint,
                headers: authHeader(),
            })
                .then((response) => {
                    if (response.status === 200) {
                        const payloadData = response.data?.payload || response.data;
                        console.log("Privileges payload: ", payloadData);
                        if (payloadData?.content) {
                            this.setPrivileges(payloadData.content);
                            this.setPrivilegesPagesCount(payloadData.totalPages || 0);
                            this.setPrivilegesDataTotal(payloadData.totalElements || 0);
                        }
                    }
                    return apiRequestsHelper.handleResponseWithDialogs(response, false);
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpGetPrivilegesPaging");
                });
        },

        httpGetPrivileges() {
            return axios({
                method: "get",
                url: `${this.rbac_endpoint}/privileges`,
                headers: authHeader(),
            })
                .then((response) => {
                    if (response.status === 200) {
                        const payloadData = response.data?.payload || response.data;
                        if (Array.isArray(payloadData)) {
                            this.setPrivilegesCatalog(payloadData);
                        }
                    }
                    return apiRequestsHelper.handleResponseWithDialogs(response, false);
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpGetPrivileges");
                });
        },

        httpCreatePrivilege(payload) {
            return axios({
                method: "post",
                url: `${this.rbac_endpoint}/privileges`,
                headers: requiredHeaders(),
                data: payload,
            })
                .then((response) => {
                    return apiRequestsHelper.handleResponseWithDialogs(response, true);
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpCreatePrivilege");
                });
        },

        httpUpdatePrivilege(payload) {
            return axios({
                method: "put",
                url: `${this.rbac_endpoint}/privileges/${payload.id}`,
                headers: requiredHeaders(),
                data: payload.data,
            })
                .then((response) => {
                    return apiRequestsHelper.handleResponseWithDialogs(response, true);
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpUpdatePrivilege");
                });
        },

        httpDeletePrivilege(payload) {
            return axios({
                method: "delete",
                url: `${this.rbac_endpoint}/privileges/${payload.id}`,
                headers: requiredHeaders(),
            })
                .then((response) => {
                    return apiRequestsHelper.handleResponseWithDialogs(response, true);
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpDeletePrivilege");
                });
        },

        httpGetPrivilegeById(payload) {
            return axios({
                method: "get",
                url: `${this.rbac_endpoint}/privileges/${payload.id}`,
                headers: authHeader(),
            })
                .then((response) => {
                    return apiRequestsHelper.handleResponseWithDialogs(response, false);
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpGetPrivilegeById");
                });
        },

        httpGetUserRolesByEmail(payload) {
            const endpoint = `${this.rbac_endpoint}/users/email/${payload.email}/roles`
            console.log("Get user roles: ", endpoint);
            return axios({
                method: "get",
                url: endpoint,
                headers: authHeader(),
            })
                .then((response) => {
                    return apiRequestsHelper.handleResponseWithDialogs(response, false);
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpGetUserRolesByEmail");
                });
        },

        httpUpdateUserRolesReplaceByEmail(payload) {
            return axios({
                method: "put",
                url: `${this.rbac_endpoint}/users/email/${payload.email}/roles`,
                headers: requiredHeaders(),
                data: {roleIds: payload.roleIds},
            })
                .then((response) => {
                    return apiRequestsHelper.handleResponseWithDialogs(response, true);
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpUpdateUserRolesReplaceByEmail");
                });
        },

        httpUpdateUserRolesAddByEmail(payload) {
            return axios({
                method: "patch",
                url: `${this.rbac_endpoint}/users/email/${payload.email}/roles`,
                headers: requiredHeaders(),
                data: {roleIds: payload.roleIds},
            })
                .then((response) => {
                    return apiRequestsHelper.handleResponseWithDialogs(response, true);
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpUpdateUserRolesAddByEmail");
                });
        },

        httpUpdateUserRolesRemoveByEmail(payload) {
            return axios({
                method: "patch",
                url: `${this.rbac_endpoint}/users/email/${payload.email}/roles/remove`,
                headers: requiredHeaders(),
                data: {roleIds: payload.roleIds},
            })
                .then((response) => {
                    return apiRequestsHelper.handleResponseWithDialogs(response, true);
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpUpdateUserRolesRemoveByEmail");
                });
        },

        httpDeleteUserByEmail(payload) {
            return axios({
                method: "delete",
                url: `${this.users_endpoint}/admin/email/${encodeURIComponent(payload.email)}`,
                headers: requiredHeaders(),
            })
                .then((response) => {
                    return apiRequestsHelper.handleResponseWithDialogs(response, true);
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpDeleteUserByEmail");
                });
        },

        httpDeleteUsersByEmails(payload) {
            return axios({
                method: "delete",
                url: `${this.users_endpoint}/admin/bulk`,
                headers: requiredHeaders(),
                data: { emails: payload.emails },
            })
                .then((response) => {
                    return apiRequestsHelper.handleResponseWithDialogs(response, true);
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpDeleteUsersByEmails");
                });
        },

        httpAdminCreateUser(createUserPayload) {
            return axios({
                method: "post",
                url: `${this.users_endpoint}/admin/create`,
                headers: requiredHeaders(),
                data: createUserPayload,
            })
                .then((response) => {
                    return apiRequestsHelper.handleResponseWithDialogs(response, true);
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpAdminCreateUser");
                });
        },
    },
});
